import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { StyleTag } from "../../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface TagSelectorProps {
  selectedTags: StyleTag[];
  onChange: (tags: StyleTag[]) => void;
}

export const TagSelector = ({ selectedTags, onChange }: TagSelectorProps) => {
  const [availableTags, setAvailableTags] = useState<StyleTag[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  // const [filteredTags, setFilteredTags] = useState<StyleTag[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { getApi } = useGeneralApiCall();

  useEffect(() => {
    fetchAvailableTags();
  }, []);

  // useEffect(() => {
  //   // Filter tags based on search term and exclude already selected ones
  //   const filtered = availableTags.filter((tag) => {
  //     const matchesSearch = tag.name
  //       .toLowerCase()
  //       .includes(searchTerm.toLowerCase());
  //     const notSelected = !selectedTags.some(
  //       (selected) => selected.tag_id === tag.tag_id
  //     );
  //     return matchesSearch && notSelected;
  //   });
  //   setFilteredTags(filtered);
  // }, [searchTerm, availableTags, selectedTags]);

  const fetchAvailableTags = async () => {
    try {
      setIsLoading(true);
      const response = await getApi("/api/style/v1/tags/");

      // The response will already have the data extracted by our interceptor
      const tags: any = response.data || [];
      setAvailableTags(tags);
    } catch (error) {
      console.error("Error fetching tags:", error);
      setAvailableTags([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter tags based on search term, but show all if search is empty
  const filteredTags = searchTerm
    ? availableTags.filter((tag) =>
        tag.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableTags;

  console.log("availbale tags", availableTags);
  console.log("filtered tags", filteredTags);

  const isTagSelected = (tag: StyleTag) =>
    selectedTags.some((t) => t.tag_id === tag.tag_id);

  const handleToggleTag = (tag: StyleTag) => {
    if (isTagSelected(tag)) {
      onChange(selectedTags.filter((t) => t.tag_id !== tag.tag_id));
    } else {
      onChange([...selectedTags, tag]);
    }
    // Keep dropdown open
    setIsDropdownOpen(true);
    setSearchTerm("");
    inputRef.current?.focus();
  };

  // const handleTagSelect = (tag: StyleTag) => {
  //   onChange([...selectedTags, tag]);
  //   setSearchTerm("");
  //   setIsDropdownOpen(true);
  //   inputRef.current?.blur();
  // };

  // const handleTagRemove = (tagId: string) => {
  //   onChange(selectedTags.filter((tag) => tag.tag_id !== tagId));
  // };

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   setSearchTerm(value);
  //   setIsDropdownOpen(value.length > 0 || filteredTags.length > 0);
  // };

  // const handleInputFocus = () => {
  //   setIsDropdownOpen(filteredTags.length > 0);
  // };

  // // const handleInputBlur = () => {
  // //   // Delay to allow clicking on dropdown items
  // //   setTimeout(() => setIsDropdownOpen(false), 200);
  // // };

  // const handleKeyDown = (e: React.KeyboardEvent) => {
  //   if (e.key === "Enter") {
  //     e.preventDefault();
  //     if (filteredTags.length > 0) {
  //       handleTagSelect(filteredTags[0]);
  //     }
  //   } else if (e.key === "Escape") {
  //     setIsDropdownOpen(false);
  //     setSearchTerm("");
  //   }
  // };

  const handleTagRemove = (tagId: string) => {
    onChange(selectedTags.filter((tag) => tag.tag_id !== tagId));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="form-group">
      {/* Tag Search Input */}
      <div className="tag-input-container" ref={dropdownRef}>
        <div className="tag-search-input">
          <input
            ref={inputRef}
            type="text"
            id="tag-selector"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            // onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder="Search and select tags..."
            className="tag-input"
            autoComplete="off"
          />
        </div>

        {/* Dropdown */}
        {isDropdownOpen && (
          <div className="tags-dropdown">
            {isLoading ? (
              <div className="dropdown-item loading">Loading tags...</div>
            ) : filteredTags.length > 0 ? (
              filteredTags.map((tag) => (
                <div
                  key={tag.tag_id}
                  className="dropdown-item tag-option"
                  onClick={() => handleToggleTag(tag)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    background: isTagSelected(tag) ? "#e6f7ff" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isTagSelected(tag)}
                    readOnly
                    style={{ marginRight: 8 }}
                  />
                  {/* <div
                    className="tag-color-indicator"
                    style={
                      tag.color ? { backgroundColor: tag.color } : undefined
                    }
                  /> */}
                  <span className="tag-name">{tag.name}</span>
                </div>
              ))
            ) : searchTerm ? (
              <div className="dropdown-item no-results">
                No tags found for "{searchTerm}"
              </div>
            ) : (
              <div className="dropdown-item info">Type to search for tags</div>
            )}
          </div>
        )}
      </div>

      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          {selectedTags.map((tag) => (
            <div
              key={tag.tag_id}
              className="tag-chip"
              style={tag.color ? { backgroundColor: tag.color } : undefined}
            >
              <span className="tag-name">{tag.name}</span>
              <button
                type="button"
                className="remove-tag"
                onClick={() => handleTagRemove(tag.tag_id)}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="tag-input-help">
        Selected: {selectedTags.length} tag
        {selectedTags.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};
